// built-from: /vision-surface v1 impl:vision-surface role:implementation
// The bespoke vision surface. EVOLVED for 056-vision-capture-method (B2, the
// screen) and RE-HOUSED by the owner-approved redesign, 2026-07-09
// (specs/056-vision-capture-method/redesign-brief.md + mockup-v2.html), on
// the 054 machinery it inherits (surface binding contract, shell-applied
// gate, optimism via the replica). PRESENTATION-LAYER change only: the
// behaviour, facts and lifecycle affordances are unchanged.
//
// RE-MAPPED to the five-stage interview map (owner-agreed interview design,
// 2026-07-09; specs/056-vision-capture-method/interview-design.md): the acts
// carry the plain names the participant hears, the crux gains its own act,
// and the two 056 facts (vision.participant, vision.north-star) render in
// their panels. Everything else stays as re-housed.
//
// Information architecture (the board, top to bottom):
//   1. PURPOSE block (goal-name typography): the framing question;
//   2. STAGE PILL TRACK: Where you are · What is in the way · The one that
//      matters · Direction · What it is worth, done/active/upcoming derived
//      PURELY from delivered state (tier + participant, landscape facts,
//      crux designation + confirmation, policy AND north star lifecycles,
//      benefits); a confirmed tactical tier never shows the two obstacle
//      pills (mirroring the Challenges visibility);
//   3. WHERE YOU ARE: the vision.participant card (who we are speaking
//      with) ahead of the vision.tier singleton; a panel until the tier
//      confirms, then the presentational agreed strip;
//   4. CHALLENGES: renders only on escalation (confirmed stream/orientation
//      tier) OR when landscape facts already stand (legacy 054
//      vision.obstacle facts render here; nothing orphaned); collapses to a
//      strip naming the crux once frame-building is the active act;
//   5. DIRECTION: the guiding-policy singleton and the north-star sentence
//      (quote treatment, its measure as the proof line), when either
//      exists; complete when BOTH confirm, and the collapsed strip quotes
//      the NORTH STAR (the destination), falling back to the policy;
//   6. BENEFITS: the destination panel; expanded whenever it is the active
//      act or holds facts (legacy vision.goal facts project as driver-less
//      objectives, approved addition #1);
//   7. the drafts tray (054, kept) as the quiet foot strip.
// A truly EMPTY session (no facts, no drafts) stages only the purpose, the
// pill track and one dashed empty-state card.
//
// The surface renders the state it is GIVEN (the shell applies the
// materialisation gate and owns append/ack/revert) and routes EVERY mutation
// through onEmitEdit. It never fetches, POSTs, or holds shared state locally
// (the only local state is view-state: the per-panel chevron override). All
// 054 behaviours hold: lifecycle text+icon tags, dashed assumed / solid
// confirmed, coral RESERVED for the crux (travelling with it into the
// collapsed Challenges strip), direct edit, one-click confirm, disputed
// blocked-open, sealed read-only, highlightFactId consumption, pulse-once,
// deselection on empty-surface click.

import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import type {
  GatedFact,
  SurfaceEdit,
  SurfaceFactState,
  SurfaceFocus,
} from '../../facilitation/surfaceBinding';
import { textOf } from './factCardKit';
import TriageBanner from './TriageBanner';
import ChallengeLandscape from './ChallengeLandscape';
import GuidingPolicyCard from './GuidingPolicyCard';
import BenefitFrame from './BenefitFrame';
import DraftsTray from './DraftsTray';
import '../../styles/VisionSurface.css';

export interface VisionSurfaceProps {
  state: SurfaceFactState;
  /** The close-in-flight freeze: affordances render but disabled. */
  busy?: boolean;
  onEmitEdit: (edit: SurfaceEdit) => void | boolean | Promise<boolean | void>;
  onPublishFocus?: (focus: SurfaceFocus) => void;
}

const ESCALATED_TIERS = new Set(['stream', 'orientation']);

// The session's acts, in board order, keyed by the same stage names the
// server's elicitation plan uses (owner-agreed interview design,
// 2026-07-09). The ACTIVE act derives purely from the delivered state: the
// tier decides until it confirms; a designated crux opens its own act until
// it is confirmed; the direction act runs until the policy AND the north
// star are both confirmed; then the session is about what it is worth.
type Act =
  | 'where-you-are'
  | 'what-is-in-the-way'
  | 'the-one-that-matters'
  | 'direction'
  | 'what-it-is-worth';
type PillState = 'done' | 'active' | 'upcoming';

const VisionSurface: React.FC<VisionSurfaceProps> = ({
  state,
  busy = false,
  onEmitEdit,
  onPublishFocus,
}) => {
  // Defence in depth on the gate: the shell delivers gated state, but a
  // rejected fact must never render even if handed to us directly.
  const rendered = state.facts.filter((f) => f.lifecycle !== 'rejected');
  const ofType = (type: string) => rendered.filter((f) => f.type === type);

  // The singletons (SET semantics: one factId each).
  const tier = rendered.find((f) => f.factId === 'vision.tier');
  const crux = rendered.find((f) => f.factId === 'vision.crux');
  const policy = rendered.find((f) => f.factId === 'vision.guiding-policy');
  // The 056 interview facts (owner-agreed interview design, 2026-07-09):
  // who we are speaking with, and gate two's north-star sentence.
  const participant = rendered.find((f) => f.factId === 'vision.participant');
  const northStar = rendered.find((f) => f.factId === 'vision.north-star');

  // The landscape: challenges plus legacy 054 obstacles (same visual family).
  const landscapeCards = rendered.filter(
    (f) => f.type === 'vision.challenge' || f.type === 'vision.obstacle',
  );
  const evidence = ofType('vision.evidence');
  const candidates = ofType('vision.crux-candidate');

  // The frame: legacy vision.goal facts ARE objectives now (driver-less),
  // approved addition #1, a client-side projection.
  const drivers = ofType('vision.driver');
  const objectives = rendered.filter(
    (f) => f.type === 'vision.objective' || f.type === 'vision.goal',
  );
  const benefits = ofType('vision.benefit');

  // Visibility: the landscape only on escalation, but standing landscape
  // facts always render (a fact on the surface never silently disappears).
  const confirmedTier =
    tier && tier.lifecycle === 'confirmed' && typeof tier.content.tier === 'string'
      ? tier.content.tier
      : null;
  const showLandscape =
    (confirmedTier !== null && ESCALATED_TIERS.has(confirmedTier))
    || landscapeCards.length > 0
    || evidence.length > 0
    || candidates.length > 0;

  // ── The active act (owner-agreed interview design, 2026-07-09) ───────────
  const policyConfirmed = policy ? policy.lifecycle === 'confirmed' : false;
  const northStarConfirmed = northStar ? northStar.lifecycle === 'confirmed' : false;
  const cruxConfirmed = crux ? crux.lifecycle === 'confirmed' : false;
  // Direction completes on BOTH peak artifacts; either alone is in progress.
  const directionComplete = policyConfirmed && northStarConfirmed;
  const directionStarted = Boolean(policy || northStar);
  let derivedAct: Act;
  if (!confirmedTier) derivedAct = 'where-you-are';
  else if (directionComplete) derivedAct = 'what-it-is-worth';
  else if (directionStarted) derivedAct = 'direction';
  else if (!ESCALATED_TIERS.has(confirmedTier)) {
    derivedAct = 'what-it-is-worth'; // legacy sessions without controller state
  } else if (!crux) derivedAct = 'what-is-in-the-way';
  else if (!cruxConfirmed) derivedAct = 'the-one-that-matters';
  else derivedAct = 'direction';
  const act = derivedAct;

  const frameHasFacts = drivers.length + objectives.length + benefits.length > 0;
  const landscapeVisible = showLandscape
    || act === 'what-is-in-the-way'
    || act === 'the-one-that-matters';
  const emptySession = rendered.length === 0 && state.drafts.length === 0;

  // The pill track: done/active/upcoming per pill, each from its own
  // completion fact; a confirmed tactical tier never shows the two obstacle
  // pills (the same visibility that hides the Challenges panel).
  const showObstaclePills = confirmedTier !== 'tactical'
    || showLandscape;
  const pillFor = (stage: Act, label: string, done: boolean) => ({
    stage,
    label,
    state: (
      act === stage
        ? 'active'
        : done
          ? 'done'
          : 'upcoming'
    ) as PillState,
  });
  const pills: Array<{ stage: string; label: string; state: PillState }> = [
    pillFor('where-you-are', 'Where you are', Boolean(confirmedTier)),
    ...(showObstaclePills
      ? [
          pillFor(
            'what-is-in-the-way',
            'What is in the way',
            Boolean(confirmedTier) && Boolean(crux || policy || northStar),
          ),
          pillFor('the-one-that-matters', 'The one that matters', cruxConfirmed),
        ]
      : []),
    pillFor('direction', 'Direction', directionComplete),
    pillFor('what-it-is-worth', 'What it is worth', false),
  ];

  const lastTurn = new Set(state.lastTurnFactIds ?? []);

  // Consuming an inbound highlight (054 kept): scroll to the referenced card
  // and move focus into it (its field when present, else the card).
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const registerRef = (factId: string, el: HTMLElement | null) => {
    if (el) cardRefs.current.set(factId, el);
    else cardRefs.current.delete(factId);
  };
  const highlightFactId = state.highlightFactId ?? null;
  useEffect(() => {
    if (!highlightFactId) return;
    const card = cardRefs.current.get(highlightFactId);
    if (!card) return;
    card.scrollIntoView({ block: 'center' });
    const field = card.querySelector<HTMLElement>('input, textarea');
    (field ?? card).focus();
  }, [highlightFactId]);

  // The policy's answered challenges resolve against the landscape cards.
  const answersTexts: string[] = (() => {
    if (!policy) return [];
    const ids = policy.content.answersChallengeFactIds;
    if (!Array.isArray(ids)) return [];
    const byId = new Map(landscapeCards.map((c: GatedFact) => [c.factId, textOf(c)]));
    return ids
      .map((id) => (typeof id === 'string' ? byId.get(id) : undefined))
      .filter((text): text is string => Boolean(text));
  })();

  const sectionCommons = {
    sealed: state.sealed,
    busy,
    lastTurn,
    registerRef,
    onEmitEdit,
    onPublishFocus,
  };

  return (
    <div
      className="vsf"
      data-testid="vision-surface"
      onClick={(e) => {
        // Clicking the empty surface publishes deselection (054 kept).
        if (e.target === e.currentTarget) onPublishFocus?.({ factId: null });
      }}
    >
      {/* The purpose block: the framing question in goal-name typography. */}
      <div className="vsf-purpose">
        <p className="vsf-purpose-lead">
          Where do you want this organisation to be, and what stands in the way?
        </p>
        <p className="vsf-purpose-sub">
          Facts land here as you talk. Confirm the ones you stand behind.
        </p>
      </div>

      {/* The stage pill track: state carried by text + icon, never colour
          alone (an sr-only state word rides each pill). */}
      <div className="vsf-pills" data-testid="vsf-pills" aria-label="Session progress">
        {pills.map((pill) => (
          <span
            key={pill.stage}
            data-stage={pill.stage}
            data-state={pill.state}
            className={`vsf-pill vsf-pill--${pill.state}`}
            {...(pill.state === 'active' ? { 'aria-current': 'step' as const } : {})}
          >
            {pill.state === 'done' && <Check size={12} aria-hidden="true" />}
            {pill.label}
            <span className="vsf-sr-only">
              {pill.state === 'done'
                ? ' (done)'
                : pill.state === 'active'
                  ? ' (current)'
                  : ' (upcoming)'}
            </span>
          </span>
        ))}
      </div>

      {emptySession ? (
        // Session start: one dashed empty-state card, no panels, no tray.
        <div className="vsf-empty" data-testid="vsf-empty">
          The board fills in as the conversation surfaces facts.
        </div>
      ) : (
        <>
          {(tier || participant || drivers.length > 0) && (
            <TriageBanner
              tier={tier}
              participant={participant}
              drivers={drivers}
              active={act === 'where-you-are'}
              sealed={state.sealed}
              busy={busy}
              pulsing={lastTurn.has('vision.tier')}
              participantPulsing={lastTurn.has('vision.participant')}
              registerRef={registerRef}
              onEmitEdit={onEmitEdit}
            />
          )}

          {landscapeVisible && (
            <ChallengeLandscape
              cards={landscapeCards}
              evidence={evidence}
              candidates={candidates}
              crux={crux}
              defaultExpanded={
                act === 'where-you-are'
                || act === 'what-is-in-the-way'
                || act === 'the-one-that-matters'
              }
              active={act === 'what-is-in-the-way' || act === 'the-one-that-matters'}
              {...sectionCommons}
            />
          )}

          {(policy || northStar || act === 'direction') && (
            <GuidingPolicyCard
              policy={policy}
              northStar={northStar}
              answersTexts={answersTexts}
              sealed={state.sealed}
              busy={busy}
              pulsing={lastTurn.has('vision.guiding-policy')}
              northStarPulsing={lastTurn.has('vision.north-star')}
              defaultExpanded={act !== 'what-it-is-worth'}
              active={act === 'direction'}
              registerRef={registerRef}
              onEmitEdit={onEmitEdit}
              onPublishFocus={onPublishFocus}
            />
          )}

          {(act === 'what-it-is-worth' || frameHasFacts) && (
            <BenefitFrame
              drivers={drivers}
              showDrivers
              objectives={objectives}
              benefits={benefits}
              defaultExpanded={act === 'what-it-is-worth' || frameHasFacts}
              active={act === 'what-it-is-worth'}
              {...sectionCommons}
            />
          )}

          <DraftsTray
            drafts={state.drafts}
            sealed={state.sealed}
            busy={busy}
            onEmitEdit={onEmitEdit}
          />
        </>
      )}
    </div>
  );
};

export default VisionSurface;
